import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Typography,
  alpha,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import axios from "../../api/axios";
import logo from "../../utils/images/ridePlus.png";
import useAuth from "../../hooks/useAuth";
import useTenantBranding from "../../hooks/useTenantBranding";
import {
  clearPersistedPostAuthReturn,
  getPersistedPostAuthReturn,
} from "../../utils/postAuthNavigation";

const CHECK_STATUS_PATH = "/oauth/check-phone-update-status";

function TelegramAuth() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const { tenantLogo, hideDefaultBranding } = useTenantBranding();
  const { setAuth, setAuthWithTenant, setCurrentTenant } = useAuth();

  const consentId = searchParams.get("consent_id") || searchParams.get("consentId");
  const tenantId = searchParams.get("tenant_id") || searchParams.get("tenantId");
  const telegramUserId = searchParams.get("telegram_user_id") || searchParams.get("telegramUserId");
  const telegramBotUserName =
    searchParams.get("telegram_bot_user_name") || searchParams.get("telegramBotUserName");
  const tenantLogoParam = searchParams.get("tenant_logo") || searchParams.get("tenantLogo");
  const tenantNameParam = searchParams.get("tenant_name") || searchParams.get("tenantName");

  const [isWaitingForPhoneShare, setIsWaitingForPhoneShare] = useState(false);
  const [isExchangingToken, setIsExchangingToken] = useState(false);
  const [pollingError, setPollingError] = useState("");

  const normalizedBotUserName = useMemo(
    () => (telegramBotUserName || "").replace(/^@/, ""),
    [telegramBotUserName]
  );

  const telegramRedirectUrl = useMemo(() => {
    if (!normalizedBotUserName) return "";
    const startPayload = tenantId ? `share_phone_${tenantId}` : "share_phone";
    return `https://t.me/${normalizedBotUserName}?start=${encodeURIComponent(startPayload)}`;
  }, [normalizedBotUserName, tenantId]);

  const validateRequiredParams = useCallback(() => {
    if (!tenantId || !telegramUserId || !normalizedBotUserName) {
      enqueueSnackbar("Missing Telegram auth parameters in URL.", { variant: "error" });
      navigate("/login", { replace: true });
      return false;
    }
    return true;
  }, [tenantId, telegramUserId, normalizedBotUserName, enqueueSnackbar, navigate]);

  const exchangeInternalToken = useCallback(async () => {
    if (!validateRequiredParams()) return;

    setIsExchangingToken(true);
    try {
      const response = await axios.post("/internalToken", {
        telegram_user_id: Number(telegramUserId),
        tenant_id: tenantId,
      }, {
        withCredentials: true,
      });

      const tokenData = response?.data?.data || response?.data;
      if (!tokenData?.access_token) {
        throw new Error("Token exchange failed");
      }

      setCurrentTenant({ id: tenantId, name: tenantNameParam || null, logo: tenantLogoParam || null });
      setAuth(tokenData);
      setAuthWithTenant(tokenData, tenantId);

      if (consentId) {
        navigate(`/consent?consentId=${encodeURIComponent(consentId)}`, { replace: true });
      } else {
        const profileReturn = getPersistedPostAuthReturn();
        if (profileReturn) {
          clearPersistedPostAuthReturn();
          navigate(profileReturn, { replace: true });
        } else {
          navigate("/", { replace: true });
        }
      }
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          "Failed to exchange token.",
        { variant: "error" }
      );
      setIsWaitingForPhoneShare(false);
    } finally {
      setIsExchangingToken(false);
    }
  }, [
    consentId,
    enqueueSnackbar,
    navigate,
    setAuth,
    setAuthWithTenant,
    setCurrentTenant,
    tenantLogoParam,
    tenantNameParam,
    telegramUserId,
    tenantId,
    validateRequiredParams,
  ]);

  const checkPhoneUpdateStatus = useCallback(async () => {
    try {
      const response = await axios.post(CHECK_STATUS_PATH, {
        telegram_user_id: Number(telegramUserId),
        tenant_id: tenantId,
      });

      const status = String(response?.data?.data?.status || response?.data?.status || "")
        .toLowerCase()
        .trim();

      if (status === "completed" || status === "success" || status === "done") {
        return { completed: true, status };
      }
      if (status === "pending") {
        return { completed: false, pending: true, status };
      }
      if (status) {
        return { completed: false, failed: true, status };
      }
    } catch {
      return { completed: false, noWorkingEndpoint: true };
    }
    return { completed: false };
  }, [telegramUserId, tenantId]);

  useEffect(() => {
    validateRequiredParams();
  }, [validateRequiredParams]);

  useEffect(() => {
    if (!isWaitingForPhoneShare || isExchangingToken) return undefined;

    let mounted = true;
    const intervalId = setInterval(async () => {
      if (!mounted) return;
      const result = await checkPhoneUpdateStatus();
      if (result?.completed) {
        clearInterval(intervalId);
        await exchangeInternalToken();
      } else if (result?.failed) {
        clearInterval(intervalId);
        setIsWaitingForPhoneShare(false);
        enqueueSnackbar(
          `Phone sharing was not completed (status: ${result.status}). Please try again.`,
          { variant: "warning" }
        );
      } else if (result?.noWorkingEndpoint) {
        setPollingError(
          "We couldn't verify your phone-sharing status right now. Please try again in a moment."
        );
      }
    }, 3000);

    return () => {
      mounted = false;
      clearInterval(intervalId);
    };
  }, [
    isWaitingForPhoneShare,
    isExchangingToken,
    checkPhoneUpdateStatus,
    exchangeInternalToken,
    enqueueSnackbar,
  ]);

  const handleSharePhoneClick = () => {
    if (!validateRequiredParams()) return;
    setPollingError("");
    setIsWaitingForPhoneShare(true);
    window.open(telegramRedirectUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <Container maxWidth="sm" sx={{ p: { xs: 0, sm: 2 }, mt: { xs: 0, sm: 4 }, minHeight: "100vh" }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 4 },
          borderRadius: { xs: 0, sm: 4 },
          background: (theme) =>
            theme.palette.mode === "light"
              ? "linear-gradient(145deg, #f8fafc 0%, #ffffff 100%)"
              : alpha(theme.palette.background.paper, 0.8),
          border: { xs: "none", sm: "1px solid" },
          borderColor: "divider",
          minHeight: { xs: "100vh", sm: "520px" },
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        {(tenantLogo || tenantLogoParam || !hideDefaultBranding) && (
          <Box
            component="img"
            src={tenantLogo || tenantLogoParam || logo}
            alt="Logo"
            sx={{ width: 90, mb: 3 }}
          />
        )}
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          Telegram Verification
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 460 }}>
          Continue in Telegram and share your phone number to complete sign-in.
        </Typography>

        {pollingError && (
          <Alert severity="warning" sx={{ mb: 2, width: "100%" }}>
            {pollingError}
          </Alert>
        )}

        {!isWaitingForPhoneShare && !isExchangingToken ? (
          <Button
            variant="contained"
            onClick={handleSharePhoneClick}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              py: 1.2,
              borderRadius: 2.5,
            }}
          >
            Open Telegram and Share Phone
          </Button>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
            <CircularProgress size={36} />
            <Typography variant="body2" color="text.secondary">
              {isExchangingToken
                ? "Phone shared. Finishing sign-in..."
                : "Waiting for phone sharing confirmation from Telegram..."}
            </Typography>
          </Box>
        )}
      </Paper>
    </Container>
  );
}

export default TelegramAuth;
